import React, { useEffect, useState } from "react";
import { BoxWrapper, CustomMenuItem, StyledMenu } from "./Styled";
import ArrowDownIcon from "../../svg/ArrowDownIcon";
import { Box, Checkbox } from "@mui/material";
import { color } from "../../styles";
import { useLocation } from "react-router-dom";

interface DropDownProps {
    menuText: string;
    handleSetAnchor: any;
    style?: any;
    isSelected?: boolean;
    disabled?: boolean;
}

const DropDown = ({
    menuText,
    handleSetAnchor,
    style,
    isSelected,
    disabled,
}: DropDownProps) => {
    return (
        <BoxWrapper
            onClick={disabled ? undefined : handleSetAnchor}
            style={{
                ...style,
                color: isSelected ? "black" : style?.color,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
            }}
        >
            <span style={{ 
                alignItems: 'center', 
                lineHeight: '1',
            }}>{menuText}</span>
            <ArrowDownIcon />
        </BoxWrapper>
    );
};

interface DropdownMenuProps {
    menuList: any[];
    menuText: string;
    handleOnclickItem: Function;
    style?: any;
    disabled?: boolean;
}

export const DropdownMenu = ({
    menuList,
    menuText,
    handleOnclickItem,
    style,
    disabled,
}: DropdownMenuProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [anchorWidth, setAnchorWidth] = useState(0);
    const open = Boolean(anchorEl);

    const longestItem =
        menuList && menuList.length > 0
            ? menuList.reduce((longest, item) => {
                  return item.text.length > longest.text.length
                      ? item
                      : longest;
              }, menuList[0])
            : { text: "" };

    // Assuming the longest item's text length is what you want for the minWidth (in pixels)
    const minWidth = longestItem.text.length * 10;

    useEffect(() => {
        if (anchorEl) {
            setAnchorWidth(anchorEl.offsetWidth);
        }
    }, [anchorEl]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!disabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <DropDown
                menuText={menuText}
                handleSetAnchor={handleClick}
                style={style}
                disabled={disabled}
            />
            <StyledMenu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button",
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        style: {
                            minWidth: minWidth,
                            width: anchorWidth,
                            maxHeight: "200px",
                            overflowY: "auto",
                        },
                    },
                }}
            >
                {menuList && menuList.length > 0 ? (
                    menuList.map((item: any) => (
                        <CustomMenuItem
                            key={item.text}
                            onClick={() => {
                                handleOnclickItem(item);
                                handleClose();
                            }}
                        >
                            {item.text}
                        </CustomMenuItem>
                    ))
                ) : (
                    <CustomMenuItem disabled>
                        Hiện không có tuỳ chọn nào
                    </CustomMenuItem>
                )}
            </StyledMenu>
        </>
    );
};

interface MultiSelectDropdownProps {
    menuList: any[];
    menuText: string;
    menuField: string;
    handleOnclickItem: Function;
    style?: any;
    isReset?: boolean;
    dataTestId?: string;
    disabled?: boolean;
}

export const DropdownMultiSelectMenu = ({
    menuList,
    menuText,
    menuField,
    handleOnclickItem,
    style,
    isReset,
    dataTestId,
    disabled,
}: MultiSelectDropdownProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [anchorWidth, setAnchorWidth] = useState(0);
    const [checkedList, setCheckedList] = useState<any>({});
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const location = useLocation();
    const open = Boolean(anchorEl);

    // Calculate width based on longest menu item text
    const longestItem = React.useMemo(() => {
        if (!menuList || menuList.length === 0) return { text: "" };
        return menuList.reduce((longest, item) => {
            return item.text.length > longest.text.length ? item : longest;
        }, menuList[0]);
    }, [menuList]);

    // Estimate width needed: text length * ~8px per character + checkbox + padding
    const estimatedMenuWidth = React.useMemo(() => {
        const textWidth = longestItem.text.length * 8;
        const checkboxWidth = 40; // Checkbox + gap
        const padding = 32; // Left + right padding
        return Math.max(anchorWidth, textWidth + checkboxWidth + padding);
    }, [longestItem.text.length, anchorWidth]);

    useEffect(() => {
        if (anchorEl) {
            setAnchorWidth(anchorEl.offsetWidth);
        }
    }, [anchorEl]);

    useEffect(() => {
        // Initialize checkedList and selectedOptions based on URL parameters
        const params = new URLSearchParams(window.location.search);
        const paramValues: { [key: string]: string } = {};

        params.forEach((value, key) => {
            if (value) {
                paramValues[key] = decodeURIComponent(value);
            }
        });

        const newCheckedList: { [key: number]: boolean } = {};
        const newSelectedOptions: string[] = [];

        menuList.forEach((item, idx) => {
            Object.values(paramValues).forEach((value) => {
                if (value && value.split(",").includes(String(item.value))) {
                    newCheckedList[idx] = true;
                    newSelectedOptions.push(item.text);
                }
            });
        });

        setCheckedList(newCheckedList);
        setSelectedOptions(newSelectedOptions);
    }, [menuList]);

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return;
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onChangeCheckbox = (event: any, idx: number, item: any) => {
        const newCheckedState = !checkedList[idx];
        setCheckedList({ ...checkedList, [idx]: newCheckedState });
        handleOnclickItem(item, newCheckedState);

        if (newCheckedState) {
            setSelectedOptions([...selectedOptions, item.text]);
        } else {
            setSelectedOptions(
                selectedOptions.filter((option) => option !== item.text)
            );
        }
    };

    const onCLickResetCheckedList = () => {
        setCheckedList({});
    };

    useEffect(() => {
        if (isReset) {
            const queryParams = new URLSearchParams(location.search);
            if (queryParams.has(menuField)) {
                const paramValues = queryParams.get(menuField)?.split(',') || [];
                setSelectedOptions(menuList
                    .filter(item => paramValues.includes(String(item.value)))
                    .map(item => item.text));
                setCheckedList(() => {
                    const newCheckedList: { [key: number]: boolean } = {};
                    menuList.forEach((item, idx) => {
                        if (paramValues.includes(String(item.value))) {
                            newCheckedList[idx] = true;
                        }
                    });
                    return newCheckedList;
                });
            } else {
                onCLickResetCheckedList();
                setSelectedOptions([]);
            }
        }
    }, [isReset]);

    return (
        <Box data-testid={dataTestId ? dataTestId : ""}>
            <DropDown
                menuText={
                    selectedOptions.length > 0
                        ? selectedOptions.join(", ")
                        : menuText
                }
                handleSetAnchor={handleClick}
                style={style}
                isSelected={selectedOptions.length > 0}
                disabled={disabled}
            />
            <StyledMenu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button",
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        style: {
                            width: Math.max(anchorWidth, estimatedMenuWidth),
                            minWidth: anchorWidth,
                            maxHeight: "200px",
                            overflowY: "auto",
                            overflowX: "visible",
                        },
                    },
                }}
            >
                {menuList && menuList.length > 0 ? (
                    menuList.map((item: any, idx: number) => (
                        <CustomMenuItem
                            key={idx}
                            onClick={() =>
                                onChangeCheckbox(
                                    { target: { checked: !checkedList[idx] } },
                                    idx,
                                    item
                                )
                            }
                        >
                            <Checkbox checked={checkedList[idx] || false} />
                            <span style={{ flexGrow: 1 }}>{item.text}</span>
                        </CustomMenuItem>
                    ))
                ) : (
                    <CustomMenuItem disabled>
                        Hiện không có tuỳ chọn nào
                    </CustomMenuItem>
                )}
            </StyledMenu>
        </Box>
    );
};

export default DropdownMenu;
